import { z } from 'zod';
import { BILLING_MANAGED_BY } from '@trulyinbox/types';
import { apiFetch } from './api';

/**
 * Billing summary — drives the sidebar plan card.
 *
 * Backend source: `apps/api/src/modules/billing/dto/billing-summary-response.dto.ts`.
 * The endpoint is `GET /billing/summary` and is open to any org member
 * (only `JwtAuthGuard`, not the OWNER guard).
 *
 * We validate the response with Zod at the API boundary so a stale or
 * mis-shaped payload surfaces as a clean query error instead of crashing the
 * sidebar at render time.
 */
export const subscriptionStatusSchema = z.enum([
  'TRIALING',
  'ACTIVE',
  'PAUSED',
  'CANCELED',
  // A payment failed and the subscription is in dunning — the owner must update
  // their payment method (P4). Both `/billing/summary` and `/billing/subscription`
  // can now return this.
  'PAST_DUE',
]);

/**
 * A pending Paddle `scheduled_change` — a cancel / pause / resume booked for the
 * end of the current billing period, mirrored locally by the
 * `subscription.updated` webhook. Backend source:
 * `SubscriptionScheduledChangeDto`, emitted with an IDENTICAL shape on
 * `GET /billing/subscription` (owner-only) and `GET /billing/summary` (any org
 * member) — hence one schema reused by both response schemas below, rather than
 * two that merely happen to match.
 *
 * This is the only thing that makes a scheduled cancel/pause visible at all:
 * Paddle applies both at `next_billing_period`, so `status` stays ACTIVE and the
 * rest of the payload looks completely untouched until the period actually ends.
 *
 * `effectiveAt` is `null` when Paddle sent no `effective_at`; consumers degrade
 * to "at period end" copy rather than printing a bare or invented date.
 */
export const subscriptionScheduledChangeSchema = z.object({
  action: z.enum(['cancel', 'pause', 'resume']),
  effectiveAt: z.string().nullable(),
});

export type SubscriptionScheduledChange = z.infer<typeof subscriptionScheduledChangeSchema>;
export type ScheduledChangeAction = SubscriptionScheduledChange['action'];

/**
 * `scheduledChange` exactly as it appears on BOTH reads. Deploy ordering can put
 * an older API build in front of this client on staging/prod, so:
 *   - `.nullish()` tolerates a backend that omits the field entirely,
 *   - `.default(null)` normalizes that absence to `null` so every downstream
 *     `== null` check sees one shape instead of `undefined | null`,
 *   - `.catch(null)` degrades an unrecognised future `action` to "nothing
 *     pending" instead of a ZodError — the same containment `banner` gets below,
 *     and for the same reason: the summary backs the sidebar plan card on every
 *     page and must never be blanked by one unexpected sub-field.
 */
const scheduledChangeField = subscriptionScheduledChangeSchema
  .nullish()
  .default(null)
  .catch(null);

/**
 * When a paused subscription comes back, or `null` when nothing is paused and
 * for an indefinite pause. Present on BOTH reads, as a top-level sibling of
 * `scheduledChange` rather than a field inside it — because ONE field has to
 * cover TWO states that `scheduledChange` cannot express together:
 *   - pause **booked**: `status` is still ACTIVE, `scheduledChange.action` is
 *     `'pause'`, and this is the date it will come back on.
 *   - pause **landed**: `status` is PAUSED and Paddle has cleared the scheduled
 *     change, so `scheduledChange` is `null` — this field is the only thing left
 *     that knows a resume is coming.
 *
 * The server already suppresses it to `null` outside those two states, so a
 * non-null value is on its own sufficient to render the resume date; no consumer
 * needs to re-derive which state it is in from `status`.
 *
 * Same forward/backward-compat treatment as `scheduledChangeField` above, and
 * for the same reason: it rides the summary that backs the sidebar plan card on
 * every page, so a backend that predates the field must degrade to "no resume
 * date" rather than failing the whole parse and blanking the rail.
 */
const scheduledResumeAtField = z.string().nullish().default(null).catch(null);

/**
 * The one derived alert the backend wants surfaced app-wide, or `null` when
 * there is nothing to say. Backend source: `BillingSummaryBannerDto` +
 * `BillingService.deriveBanner`, which owns the PRECEDENCE (SUBSCRIPTION_PAUSED ▸
 * PAYMENT_FAILED ▸ SUBSCRIPTION_SCHEDULED_CHANGE ▸ TRIAL_ENDING ▸
 * TRIAL_EXTENSION_AVAILABLE ▸ FREE_PLAN_UPGRADE ▸ none). The client renders
 * whatever `kind` arrives and never re-derives that ordering.
 *
 * `SUBSCRIPTION_SCHEDULED_CHANGE` fires only for a pending **cancel** or
 * **pause** — a scheduled *resume* loses the user nothing, so the backend emits
 * `banner: null` for it while still reporting it on `summary.scheduledChange`.
 *
 * `kind` is a plain `z.enum` rather than a `z.discriminatedUnion` because all
 * kinds carry the IDENTICAL payload — a union would be structurally identical
 * members, which buys no narrowing and diverges from the flat `z.object` +
 * `z.enum` shape every other schema in this file uses
 * (`subscriptionStatusSchema` is the precedent).
 *
 * `graceEndsAt` is the deadline the banner counts down to and means a different
 * thing per kind: PAST_DUE → end of the dunning grace; SUBSCRIPTION_SCHEDULED_CHANGE
 * → the effective date of the pending cancel/pause; TRIAL_ENDING → the trial
 * end; TRIAL_EXTENSION_AVAILABLE → when the extend-a-lapsed-trial offer closes
 * (a REAL deadline, unlike the one below — after it the CTA is gone);
 * FREE_PLAN_UPGRADE → when the nudge stops showing (NOT a user-facing
 * deadline — see `billing-banner.ts`). It is always `null` for
 * SUBSCRIPTION_PAUSED: a pause is indefinite, so there is no deadline to show.
 *
 * `message` is the backend's own copy. It is parsed rather than dropped so the
 * field can't silently disappear again, but the banner renders client-owned copy
 * instead: the server string embeds a raw ISO date and assumes the reader is the
 * owner (see `features/billing/billing-banner.ts`).
 */
export const billingBannerKindSchema = z.enum([
  'SUBSCRIPTION_PAUSED',
  'PAYMENT_FAILED',
  'SUBSCRIPTION_SCHEDULED_CHANGE',
  'TRIAL_ENDING',
  'TRIAL_EXTENSION_AVAILABLE',
  'FREE_PLAN_UPGRADE',
]);

export const billingBannerSchema = z.object({
  kind: billingBannerKindSchema,
  graceEndsAt: z.string().nullable(),
  trialExtensionAvailable: z.boolean(),
  message: z.string(),
});

export type BillingBannerKind = z.infer<typeof billingBannerKindSchema>;
export type BillingBanner = z.infer<typeof billingBannerSchema>;

/**
 * TRU-540 — four-stage usage nudges.
 *
 * Backend source: `usage-nudge.constants.ts` (`USAGE_LIMIT_CODE`, `UsageTier`,
 * `UsageBannerKind`) + `BillingUsageItemDto` / `BillingUsageBannerDto`. These
 * three enums are declared ONCE here and imported everywhere else on the
 * client — `features/billing/usage-nudge.ts` derives every one of its maps from
 * them, so adding a fifth tier is a single edit that fails the build at each
 * `Record<UsageTier, …>` rather than silently rendering a blank tone.
 *
 * Exactly two limits are evaluated: connected mailboxes and the org-wide daily
 * warmup budget. There is no seat/workspace limit in this product.
 */
export const usageLimitCodeSchema = z.enum(['EMAIL_ACCOUNTS', 'DAILY_EMAILS']);

/**
 * Threshold band, highest match wins: REACHED ≥100, CRITICAL ≥90, WARNING ≥80,
 * APPROACHING ≥70. The SERVER owns this derivation — the client never
 * recomputes a tier from `pct`, it only decides how a given tier looks.
 */
export const usageTierSchema = z.enum([
  'APPROACHING',
  'WARNING',
  'CRITICAL',
  'REACHED',
]);

/**
 * There is deliberately no `USAGE_APPROACHING` kind: 70% is the subtle
 * in-app-indicator tier, so it rides `usage[]` and produces no banner at all.
 */
export const usageBannerKindSchema = z.enum([
  'USAGE_WARNING',
  'USAGE_CRITICAL',
  'USAGE_REACHED',
]);

/**
 * One org-level limit's evaluated usage. Both entries are always present, in
 * `EMAIL_ACCOUNTS`-then-`DAILY_EMAILS` order.
 *
 * `used` is NEVER null — `0` means genuinely zero, so it renders as an empty
 * meter, never as a dash. `limit: null` means UNLIMITED and forces `pct` and
 * `tier` to null too: there is nothing to be a percentage of and nothing to
 * nudge about. Consumers must never coerce a null limit to 0.
 */
export const billingUsageItemSchema = z.object({
  code: usageLimitCodeSchema,
  used: z.number(),
  limit: z.number().nullable(),
  pct: z.number().nullable(),
  tier: usageTierSchema.nullable(),
});

/**
 * The single highest-severity usage banner, or `null` when no limit is at 80%+.
 *
 * `used` / `limit` / `pct` are all non-null here by construction — an unlimited
 * limit can never win a banner — which is why this is its own schema rather
 * than a reuse of `billingUsageItemSchema` with nullable fields.
 *
 * `message` is the backend's own copy and IS rendered (unlike `banner.message`
 * above, which the client replaces): it carries no raw ISO date and reads
 * correctly as-is. It is still viewer-dependent — see `usage-nudge.ts` for the
 * one case (a non-owner, who has no upgrade path) where the client composes its
 * own sentence instead.
 */
export const billingUsageBannerSchema = z.object({
  kind: usageBannerKindSchema,
  code: usageLimitCodeSchema,
  used: z.number(),
  limit: z.number(),
  pct: z.number(),
  message: z.string(),
});

/**
 * Today's org-wide warmup sending usage.
 *
 * `used` and `remaining` are DIFFERENT QUANTITIES from different stores, and the
 * distinction is load-bearing: `used` is a durable MySQL counter incremented
 * after each successful send (always a number), while `remaining` is live
 * enforcement HEADROOM read from Redis, which may be null, negative, or briefly
 * exceed `limit`. `used + remaining` need not equal `limit`. Every meter and
 * percentage must therefore be driven off `used`/`limit` (or the server's
 * `pct`), NEVER off `remaining`.
 */
/** The caller's current workspace's share of the daily budget. `used: null` = unknown. */
export const workspaceDailySchema = z.object({
  workspaceId: z.number(),
  workspaceName: z.string(),
  limit: z.number().nullable(),
  used: z.number().nullable(),
});

export const warmupDailySchema = z.object({
  limit: z.number().nullable(),
  used: z.number(),
  remaining: z.number().nullable(),
  unlimited: z.boolean(),
  resetsAt: z.string(),
  workspace: workspaceDailySchema.nullish().default(null).catch(null),
});

export type UsageLimitCode = z.infer<typeof usageLimitCodeSchema>;
export type UsageTier = z.infer<typeof usageTierSchema>;
export type UsageBannerKind = z.infer<typeof usageBannerKindSchema>;
export type BillingUsageItem = z.infer<typeof billingUsageItemSchema>;
export type BillingUsageBanner = z.infer<typeof billingUsageBannerSchema>;
export type WarmupDaily = z.infer<typeof warmupDailySchema>;
export type WorkspaceDaily = z.infer<typeof workspaceDailySchema>;

export const billingSummarySchema = z.object({
  plan: z
    .object({
      id: z.number(),
      name: z.string(),
      // Base (parent) plan id when the org is on a custom plan; null for a base
      // plan. Custom plans are never listed in /billing/plans, so the UI marks
      // the parent base plan as the current row (`parentId ?? id`). Optional so a
      // backend that predates the field still validates.
      parentId: z.number().int().nullable().optional(),
    })
    .nullable(),
  status: subscriptionStatusSchema,
  trial: z
    .object({
      startedAt: z.string(),
      endsAt: z.string(),
      daysLeft: z.number(),
    })
    .nullable(),
  emailAccounts: z.object({
    used: z.number(),
    limit: z.number().nullable(),
  }),
  // The summary is the ONLY read behind the sidebar plan card and the app-wide
  // banner, so a banner problem must never take the summary down with it:
  //   - `.optional()` tolerates a backend that predates the field (this schema
  //     silently stripped `banner` for the whole time the server was emitting
  //     it — the omission is what kept the feature dark, so the field is now
  //     explicit).
  //   - `.catch(null)` degrades an unrecognised future `kind` (or any
  //     mis-shaped banner) to "no banner" instead of a ZodError that would blank
  //     the sidebar plan card on every page.
  banner: billingBannerSchema.nullable().optional().catch(null),
  // Carried on the MEMBER-visible read (not only the owner-only subscription
  // detail) so the sidebar plan card and the app-wide banner can surface a
  // pending cancel/pause to everyone in the org, not just the owner.
  scheduledChange: scheduledChangeField,
  // Rides the member-visible read for the same reason `scheduledChange` does:
  // the sidebar plan card must be able to say when a pause ends without the
  // owner-only subscription read.
  scheduledResumeAt: scheduledResumeAtField,
  // TRU-540. All three get the SAME containment `banner` has above, for the same
  // reason: this one read backs the sidebar plan card on every page, and a
  // ZodError anywhere in the object fails the WHOLE parse — blanking the rail
  // app-wide. That has already shipped once on this endpoint. So a malformed or
  // absent usage payload must cost only the nudge, never the card:
  //   - `.nullish().default(…)` tolerates a backend that predates the field
  //     (deploy ordering routinely puts an older API in front of this client on
  //     staging) and normalizes `undefined` to one shape, so downstream code has
  //     a single absence check instead of `undefined | null`.
  //   - `.catch(…)` degrades an unrecognised future `tier` / `kind` — or any
  //     mis-shaped entry — to "nothing to nudge about".
  // `usage` degrades to `[]` (not a partial array): the array is parsed
  // all-or-nothing, and half a usage set would render a meter that silently
  // contradicts the banner. `[]` is already the "no entry for this code" path
  // every consumer handles.
  //
  // `.optional().default(…)` rather than `.nullish()`: the output type must be
  // exactly `BillingUsageItem[]`, never `| null`, so no consumer has to write a
  // second absence check for a field whose empty state is already `[]`. An
  // explicit `null` from the backend still lands on `[]` — it fails the inner
  // array parse and `.catch` rescues it — so absence has ONE shape either way.
  usage: z
    .array(billingUsageItemSchema)
    .optional()
    .default(() => [])
    .catch(() => []),
  usageBanner: billingUsageBannerSchema.nullish().default(null).catch(null),
  // `null` here means "daily usage unknown", NOT "unlimited" and NOT "zero" —
  // the daily card renders nothing rather than inventing a 0/limit meter.
  warmupDaily: warmupDailySchema.nullish().default(null).catch(null),
});

export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;
export type BillingSummary = z.infer<typeof billingSummarySchema>;

export async function getBillingSummary(): Promise<BillingSummary> {
  const raw = await apiFetch<unknown>('/billing/summary');
  return billingSummarySchema.parse(raw);
}

/**
 * Resource-access capabilities for the current user on their current plan.
 *
 * Backend source: `GET /billing/capabilities` (auth required, same gate as the
 * other `/billing/*` reads). The response `data` is `{ identifiers: string[] }`
 * where each identifier is a `resource/action` string, e.g.
 * `"email-account/connect"` or `"billing/subscription-modify"`.
 *
 * We validate at the API boundary so a mis-shaped payload surfaces as a clean
 * query error instead of crashing the consumer at render time.
 */
export const capabilitiesResponseSchema = z.object({
  identifiers: z.array(z.string()),
});

export type CapabilitiesResponse = z.infer<typeof capabilitiesResponseSchema>;

export async function getCapabilities(): Promise<string[]> {
  const raw = await apiFetch<unknown>('/billing/capabilities');
  return capabilitiesResponseSchema.parse(raw).identifiers;
}

/**
 * A `decimal(10,2)` money column. MySQL's driver may serialize it as a JS
 * `number` or a numeric `string` (`"29.00"`), so we accept both and coerce to a
 * number at the boundary. Callers get a plain `number`.
 */
const moneySchema = z.coerce.number();

/**
 * Plan catalog — drives the self-serve Upgrade surface.
 *
 * Backend source: `GET /billing/plans` (auth required). The endpoint returns
 * public plans only and stamps each with `isPublic` (the card still filters on
 * it defensively). `paddleCheckoutPriceId` is the Paddle.js price id for the
 * checkout overlay (null when the plan isn't purchasable). `paddlePlanId` is
 * the Paddle product id used for webhook resolution — not used by the client
 * checkout.
 *
 * Validated at the boundary so a mis-shaped payload surfaces as a clean query
 * error instead of crashing the upgrade card at render time.
 */
export const billingPlanSchema = z.object({
  id: z.number(),
  name: z.string(),
  isPublic: z.boolean(),
  paddleCheckoutPriceId: z.string().nullable(),
  // Numeric limits are sourced from `planRestrictions` rows (the `plans.*`
  // limit columns are gone), and a MISSING restriction row means UNLIMITED — the
  // backend emits `null`, not a sentinel number. All three must be `.nullable()`
  // for the same reason `subscriptionLimitsSchema` below is: `z.array(...).parse`
  // is all-or-nothing, so one unlimited plan would otherwise fail the ENTIRE
  // catalog and take the whole upgrade surface down. Consumers render `null` as
  // "Unlimited", never coerce it to 0.
  maxEmailAccounts: z.number().nullable(),
  aggregateDailyWarmup: z.number().nullable(),
  maxWarmupPerAccount: z.number().nullable(),
  // Billing-redesign catalog fields (2026-07-05 spec §4.2). Marked
  // optional/nullable so the schema validates against BOTH the current
  // `PlanResponseDto` (which does not yet emit them) and the future one that
  // will — the read-side DTO must be extended before these populate. See the
  // report accompanying this change for the exact backend gap.
  code: z.string().nullable().optional(),
  parentId: z.number().int().nullable().optional(),
  basePrice: moneySchema.optional(),
  currency: z.string().optional(),
  validityInDays: z.number().int().nullable().optional(),
  // Max warmup reply-rate cap (%) for the tier, 0–100; `null` = uncapped. Same
  // forward/back-compat treatment as the redesign fields above: `.optional()`
  // tolerates a backend that predates it, `.nullable()` accepts the explicit
  // uncapped `null`. The pricing card omits the reply-rate line when it's absent.
  replyRateCap: z.number().nullable().optional(),
  /**
   * The distinct `resource/action` capability identifiers this plan grants,
   * sorted, `[]` when none (the `free` plan grants zero today).
   *
   * PLAN-CATALOG data with no org / role / status dimension — it answers "which
   * plan unlocks X" for the locked-feature upsell (`resolveUnlockingPlan` in
   * `plan-helpers.ts`). It is NOT an entitlement check: the current user's own
   * capabilities still come from `GET /billing/capabilities` via
   * `useCapabilities()`.
   *
   * `.default([])` rather than `.optional()` on purpose — web can deploy ahead of
   * api and the billing page must stay alive, but every consumer gets a real
   * array so no call site needs a null check.
   */
  resourceIdentifiers: z.array(z.string()).default([]),
});

export type BillingPlan = z.infer<typeof billingPlanSchema>;

export async function listPlans(): Promise<BillingPlan[]> {
  const raw = await apiFetch<unknown>('/billing/plans');
  return z.array(billingPlanSchema).parse(raw);
}

/**
 * Current subscription — the OWNER-only billing detail read.
 *
 * Backend source: `apps/api/src/modules/billing/dto/subscription-response.dto.ts`
 * (`GET /billing/subscription`, `@RequiresOrgRole(OWNER)`). The endpoint 403s
 * for non-owners, so the consuming hook is gated on org role.
 *
 * The per-org **price** (`unitPrice` + `currency`) and **access-expiry**
 * (`expiresAt`; `null` for the free/perpetual plan) come from the subscription
 * aggregate (2026-07-05 spec §4.2). They are optional/nullable here for the same
 * reason as the plan catalog fields above — the current `SubscriptionResponseDto`
 * does not emit them yet, so the card renders a placeholder until it does.
 */
export const subscriptionLimitsSchema = z.object({
  // All three metered limits are `null` when the plan grants an unlimited
  // allowance (the backend emits `null`, not a sentinel number). Consumers must
  // render `null` as "Unlimited", never coerce it to 0.
  maxEmailAccounts: z.number().nullable(),
  aggregateDailyWarmup: z.number().nullable(),
  maxWarmupPerAccount: z.number().nullable(),
});

/**
 * The discount currently live on the org's Paddle subscription — a **DISPLAY
 * HINT** on `GET /billing/subscription`, `null` when there is none. Backend
 * source: `apps/api/src/modules/billing/dto/active-discount.dto.ts`.
 *
 * Paddle holds exactly ONE discount per subscription, so both promo surfaces
 * (the upgrade dialog's field and `RedeemCodeRow`) are blocked while this is
 * non-null, and `endsAt` is when they re-open — **`endsAt: null` means a
 * LIFETIME discount that never frees up**, not "unknown".
 *
 * Every field except `endsAt` comes from the mirrored local catalog row and is
 * `null` for a discount created straight in the Paddle dashboard, so the copy
 * helper must degrade to naming only the end date.
 *
 * It is a HINT, not an authority: the backend also emits `null` when Paddle is
 * unreachable, so a submit can still come back `041310 DiscountAlreadyActive`.
 * Both call sites handle that error path regardless of what this said.
 */
export const activeDiscountSchema = z.object({
  code: z.string().nullable(),
  // `.catch(null)` scoped to the field, not the object: an unrecognised future
  // discount type must only cost us the "20% off" phrasing, not the end date
  // that tells the customer when they can apply a new code.
  discountType: z.enum(['percentage', 'fixed']).nullable().catch(null),
  // Percentage (0–100) or a fixed major-unit amount, per `discountType`. Reuses
  // `moneySchema` because the fixed variant is a `decimal` column that may
  // serialize as a numeric string; `null` short-circuits the coercion.
  discountValue: moneySchema.nullable(),
  currency: z.string().nullable(),
  endsAt: z.string().nullable(),
});

export type ActiveDiscount = z.infer<typeof activeDiscountSchema>;

/**
 * `activeDiscount` as it rides on the subscription read. Same forward/backward
 * -compat treatment as `scheduledChangeField` above, and for the same reason:
 * this read backs the whole owner billing surface, so a backend that predates
 * the field (`.nullish().default(null)`) or one that emits a mis-shaped hint
 * (`.catch(null)`) must degrade to "no discount" rather than blanking the card.
 * Degrading open is safe — the server still rejects a second code with 041310.
 */
const activeDiscountField = activeDiscountSchema.nullish().default(null).catch(null);

export const subscriptionSchema = z.object({
  plan: z.object({
    id: z.number(),
    name: z.string(),
    // Stable plan code (`'free'`, …). Keys free-plan detection on the detail
    // card, never the display name. Optional for forward/backward-compat.
    code: z.string().nullable().optional(),
    // Base (parent) plan id when on a custom plan; null for a base plan. Drives
    // the "Custom" badge on the plan detail card and the base-plan-as-current
    // resolution in the upgrade list. Optional for forward/backward-compat.
    parentId: z.number().int().nullable().optional(),
  }),
  status: subscriptionStatusSchema,
  paddleSubscriptionId: z.string().nullable(),
  paddleCustomerId: z.string().nullable(),
  // Who owns plan changes. Anything but SELF_SERVE locks every plan CTA (the API
  // refuses checkout with 040007 / 040008). Optional so an older backend that
  // doesn't send it still parses — absent reads as self-serve.
  billingManagedBy: z
    .enum([BILLING_MANAGED_BY.SELF_SERVE, BILLING_MANAGED_BY.SALESHANDY, BILLING_MANAGED_BY.INTERNAL])
    .optional(),
  limits: subscriptionLimitsSchema,
  // Billing-redesign subscription-aggregate fields (see note above). An org
  // WITHOUT a subscription row (the default pre-backfill state) gets
  // `unitPrice: null` + `currency: null` from the backend, so both must be
  // `.nullable().optional()`: `.optional()` alone rejects an explicit `null`
  // (ZodError → the card's "Failed to load" state for every owner). `null`
  // short-circuits the `moneySchema` coercion, so a pre-backfill null stays
  // `null` (not `Number(null) === 0` → a false "$0.00").
  unitPrice: moneySchema.nullable().optional(),
  currency: z.string().nullable().optional(),
  // The customer's LOCKED recurring charge, read live from Paddle on every
  // request and never stored: MAJOR units of `recurringCurrency`, tax-EXCLUSIVE
  // (`total − tax`) — the same basis the localized pricing grid renders, so the
  // two surfaces can't quote different numbers for the same subscription.
  //
  // DISTINCT from `unitPrice`/`currency` above, which stay the catalog USD
  // snapshot the upgrade comparison runs on. Never repoint one at the other.
  //
  // The two are null TOGETHER — no Paddle subscription, Paddle unreachable, or
  // a PAUSED/CANCELED subscription that exposes no recurring charge. That last
  // one is an ordinary production state, not an edge case, so the fallback to
  // `unitPrice`/`currency` is a real path (see `formatSubscriptionPrice`).
  // Same `.nullable().optional()` forward/back-compat treatment as the redesign
  // fields above: `.optional()` alone would reject the explicit `null`.
  recurringAmount: moneySchema.nullable().optional(),
  recurringCurrency: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
  validityInDays: z.number().int().nullable().optional(),
  billingCycle: z.string().nullable().optional(),
  currentPeriodStart: z.string().nullable().optional(),
  currentPeriodEnd: z.string().nullable().optional(),
  // Dunning grace window (P4). `inGrace` is true while a PAST_DUE subscription
  // still has access (`expiresAt + grace` is in the future) and `graceEndsAt` is
  // that instant — `null` when not PAST_DUE or `expiresAt` is unknown. Both are
  // `.optional()` for the same forward/backward-compat reason as the fields
  // above: a backend that predates them must not fail the whole read.
  inGrace: z.boolean().optional(),
  graceEndsAt: z.string().nullable().optional(),
  // A cancel/pause booked for the period end. The subscription is still ACTIVE
  // (and fully paid for) while this is set — it is the ONLY signal that anything
  // is about to change, and the precondition the lifecycle actions gate on.
  scheduledChange: scheduledChangeField,
  // When a fixed-length pause ends. Set while a pause is booked AND after it has
  // landed — see `scheduledResumeAtField` for why one field spans both states.
  scheduledResumeAt: scheduledResumeAtField,
  // The discount already live at Paddle — gates both promo surfaces. See
  // `activeDiscountSchema` above for why it is only a hint.
  activeDiscount: activeDiscountField,
});

export type SubscriptionLimits = z.infer<typeof subscriptionLimitsSchema>;
export type Subscription = z.infer<typeof subscriptionSchema>;

export async function getSubscription(): Promise<Subscription> {
  const raw = await apiFetch<unknown>('/billing/subscription');
  return subscriptionSchema.parse(raw);
}

/**
 * Checkout intent — `POST /billing/checkout` (OWNER-only). Returns the Paddle
 * price id + custom data to hand straight to `paddle.Checkout.open(...)`. There
 * is NO synchronous plan change here: the Paddle webhook assigns the plan after
 * payment is confirmed.
 */
export const checkoutResponseSchema = z.object({
  priceId: z.string(),
  // `tolt_referral` is present only when the org signed up under an affiliate
  // referral. Declared (not passthrough) because zod strips unknown keys, which
  // would silently drop it before it reached `Paddle.Checkout.open`.
  customData: z.object({ orgId: z.number(), tolt_referral: z.string().optional() }),
  // RETAINED FOR COMPATIBILITY, but the server no longer populates it: auto-apply
  // at checkout was removed, so a discount reaches Paddle ONLY via a code the
  // customer types (see `validateCheckoutCode`, whose `paddleDiscountId` the
  // caller threads into `Paddle.Checkout.open`). Kept `.optional()` so an older
  // server that still sends it doesn't fail the schema.
  discountId: z.string().optional(),
});

export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;

/**
 * Validate a promo code against the plan being purchased and preview its savings
 * — `POST /billing/checkout/validate-code` (OWNER-only).
 *
 * Read-only: nothing is redeemed by this call. Paddle counts the redemption when
 * checkout actually completes, so validating early only means the customer learns
 * a code is unusable before they reach Paddle's overlay rather than after.
 *
 * `paddleDiscountId` is what the caller threads into `Paddle.Checkout.open`.
 * `savings`/`total` are MAJOR units, already computed against the plan's list
 * price (a fixed discount is capped at the price, so `total` is never negative).
 *
 * Failure modes to branch on by `errorCode` — never message text — all under
 * `ErrorCode.Billing.Discount`: `041301 NotFound`, `041302 Inactive` (also covers
 * expired / not-yet-started), `041303 NotApplicable` (wrong org or wrong plan),
 * `041309 AlreadyRedeemed`, `041305 PaddleSyncFailed`.
 */
export const validateCheckoutCodeResponseSchema = z.object({
  paddleDiscountId: z.string(),
  code: z.string(),
  discountType: z.enum(['percentage', 'fixed']),
  discountValue: z.number(),
  savings: moneySchema,
  total: moneySchema,
  currency: z.string(),
});

export type ValidateCheckoutCodeResponse = z.infer<typeof validateCheckoutCodeResponseSchema>;

export async function validateCheckoutCode(params: {
  code: string;
  planId: number;
}): Promise<ValidateCheckoutCodeResponse> {
  const raw = await apiFetch<unknown>('/billing/checkout/validate-code', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return validateCheckoutCodeResponseSchema.parse(raw);
}

export async function createCheckout(planId: number): Promise<CheckoutResponse> {
  const raw = await apiFetch<unknown>('/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ planId }),
  });
  return checkoutResponseSchema.parse(raw);
}

/**
 * Paddle customer-portal session — `POST /billing/customer-portal` (OWNER-only,
 * empty body, 200). Returns a short-lived Paddle-hosted portal URL where the
 * owner manages payment method, downgrades, pause/resume, and cancellation —
 * everything the in-app upgrade flow deliberately doesn't do.
 *
 * Failure modes the caller must expect (branch on `errorCode`, never message):
 *   - `040501 NoActiveSubscription` — the org has no Paddle customer id (never
 *     billed), so **no portal exists**. Expected, not exceptional: an org can
 *     reach `portal` mode purely because its subscription read errored.
 *   - `040502 PortalUrlFailed` — Paddle rejected the session request. Transient.
 *
 * `portalUrl` is validated rather than a bare string: the response feeds straight
 * into a top-level navigation, so a malformed/empty value must fail loudly at the
 * boundary instead of navigating the tab to garbage. `.url()` alone only checks
 * parseability — it accepts `javascript:` / `data:` — so the `https:` refinement
 * is what actually constrains the scheme to a real portal URL (defense-in-depth:
 * the value comes from our own authenticated Paddle round-trip, but the guard must
 * match the comment's promise even if the backend is ever mis-wired).
 */
export const customerPortalResponseSchema = z.object({
  portalUrl: z
    .string()
    .url()
    .refine((u) => u.startsWith('https://'), 'portalUrl must be an https URL'),
});

export type CustomerPortalResponse = z.infer<typeof customerPortalResponseSchema>;

export async function createCustomerPortalSession(): Promise<CustomerPortalResponse> {
  const raw = await apiFetch<unknown>('/billing/customer-portal', {
    method: 'POST',
  });
  return customerPortalResponseSchema.parse(raw);
}

/**
 * Proration preview for a self-serve upgrade (P2, spec §8) —
 * `GET /billing/subscription/preview?planId=<int>` (OWNER-only). Returns the
 * money breakdown Paddle would charge for switching to `planId` *right now*.
 * All amounts are in **major units** (dollars, not cents). `nextBilledAt` is
 * `null` when there is no scheduled next charge (e.g. a canceling subscription).
 *
 * Validated at the boundary so a mis-shaped payload surfaces as a clean query
 * error instead of a NaN price in the confirm dialog. Money fields reuse
 * `moneySchema` (coerces a `decimal` string → number).
 */
export const previewResponseSchema = z.object({
  subtotal: moneySchema,
  tax: moneySchema,
  credit: moneySchema,
  discount: moneySchema,
  amountToPay: moneySchema,
  currency: z.string(),
  nextBilledAt: z.string().nullable(),
  // `true` when Paddle charges the card immediately (render the money breakdown
  // as "charged today"); `false` when the prorated amount rolls into the next
  // invoice — the money fields are all 0, so the UI must render "prorated on
  // your next bill" (using `nextBilledAt`), NOT a misleading "$0.00 now".
  hasImmediateCharge: z.boolean(),
});

export type PreviewResponse = z.infer<typeof previewResponseSchema>;

/**
 * `code` recomputes the preview WITH the promo code applied, so `discount` and
 * `amountToPay` come back as Paddle's real discounted figures — the client must
 * render those, never a locally-estimated saving. It is omitted from the query
 * string when blank (the server treats an empty string as no code anyway, but
 * sending `code=` would needlessly fork the cache key its caller derives).
 *
 * A rejected code fails the whole request with a `Billing.Discount.*` code
 * (`041301` NotFound, `041302` Inactive, `041303` NotApplicable, `041309`
 * AlreadyRedeemed, `041310` DiscountAlreadyActive, `041311` UsageLimitReached,
 * `041305` PaddleSyncFailed) — the caller must tell those apart from a genuine
 * preview failure (`041006`) and surface them on the field, not as a dead end.
 */
export async function previewUpgrade(
  planId: number,
  code?: string,
): Promise<PreviewResponse> {
  const query = new URLSearchParams({ planId: String(planId) });
  const trimmed = code?.trim();
  if (trimmed) query.set('code', trimmed);
  const raw = await apiFetch<unknown>(`/billing/subscription/preview?${query.toString()}`);
  return previewResponseSchema.parse(raw);
}

/**
 * Self-serve upgrade — `PATCH /billing/subscription` body `{ planId }`
 * (OWNER-only). The plan change is applied **asynchronously via the Paddle
 * webhook**, so the synchronous response only acknowledges that the change is
 * in flight (`{ status: 'processing' }`). Callers poll `GET /billing/subscription`
 * until `plan.id` reflects the target. `status` is typed as a plain string so a
 * future backend status value doesn't reject at the boundary.
 */
export const upgradeResponseSchema = z.object({
  status: z.string(),
});

export type UpgradeResponse = z.infer<typeof upgradeResponseSchema>;

export async function upgradeSubscription(
  planId: number,
  code?: string,
): Promise<UpgradeResponse> {
  // Omit `code` entirely when blank rather than sending `""` — the same
  // rejections `previewUpgrade` can raise apply here, and an empty key would
  // make a "no code" upgrade indistinguishable from a coded one on the wire.
  const trimmed = code?.trim();
  const raw = await apiFetch<unknown>('/billing/subscription', {
    method: 'PATCH',
    body: JSON.stringify(trimmed ? { planId, code: trimmed } : { planId }),
  });
  return upgradeResponseSchema.parse(raw);
}

/**
 * Self-serve promo-code redemption — `POST /billing/subscription/redeem-code`
 * body `{ code }` (OWNER-only). The discount is applied at Paddle and surfaces
 * on the org's **next invoice**, so — like the upgrade PATCH above — the current
 * subscription payload does NOT change synchronously: the response only
 * acknowledges the code was accepted and the change is in flight
 * (`{ status: 'processing' }`). `status` is a plain string so a future backend
 * value doesn't reject at the boundary.
 *
 * A dedicated schema (rather than reusing `upgradeResponseSchema`) because this
 * is a distinct endpoint contract that can diverge — the two only share a
 * surface `{ status }` shape, matching the one-schema-per-endpoint pattern the
 * rest of this file uses.
 *
 * Failure modes the caller branches on by `errorCode` (never message text) live
 * under `ErrorCode.Billing.Discount`: `041301 NotFound`, `041302 Inactive`,
 * `041303 NotApplicable`, `041306 NoPaddleSubscription`, `041305 PaddleSyncFailed`.
 */
export const redeemCodeResponseSchema = z.object({
  status: z.string(),
});

export type RedeemCodeResponse = z.infer<typeof redeemCodeResponseSchema>;

export async function redeemCode(code: string): Promise<RedeemCodeResponse> {
  const raw = await apiFetch<unknown>('/billing/subscription/redeem-code', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return redeemCodeResponseSchema.parse(raw);
}

/**
 * One-time 7-day trial extension — `POST /billing/subscription/extend-trial`
 * (OWNER-only, empty body, 200).
 *
 * Unlike every other lifecycle write in this file, this one is **not** proxied
 * to Paddle: a trial has no Paddle subscription, so the change is applied
 * locally and synchronously and the response is authoritative. Callers must NOT
 * poll `GET /billing/subscription` afterwards (the cancel/pause/resume pattern)
 * — just invalidate the summary + subscription keys.
 *
 * `daysAdded` is added to the previous trial END, not to `now`, so `trialEndsAt`
 * is the new end date to surface. `extensionAvailable` is always `false` on
 * success — the extension is one-time.
 *
 * Failure modes the caller must branch on by `errorCode` (all 422):
 *   - `041201 NotTrialing`        — no active trial to extend.
 *   - `041202 AlreadyExtended`    — the one-time extension is spent. Expected,
 *     not exceptional (a stale cached banner still offering the CTA lands here),
 *     so it deserves its own copy rather than a generic red toast.
 *   - `041203 TrialNotExtendable` — trial data is incomplete; support-only.
 */
export const trialExtensionResponseSchema = z.object({
  trialEndsAt: z.string(),
  daysAdded: z.number(),
  extensionAvailable: z.boolean(),
});

export type TrialExtensionResponse = z.infer<typeof trialExtensionResponseSchema>;

export async function extendTrial(): Promise<TrialExtensionResponse> {
  const raw = await apiFetch<unknown>('/billing/subscription/extend-trial', {
    method: 'POST',
  });
  return trialExtensionResponseSchema.parse(raw);
}

/**
 * Subscription lifecycle actions —
 * `POST /billing/subscription/{cancel,pause,resume,revoke-scheduled-change}`
 * (OWNER-only; only a fixed-length **pause** carries a body — see
 * `runLifecycleAction`).
 *
 * All four are **Paddle proxies that mutate nothing locally**: the API validates
 * preconditions, asks Paddle to act, and returns `{ status: 'processing' }`. The
 * `subscription.canceled/paused/resumed/updated` webhook is the single writer of
 * the resulting local state, so callers must POLL `GET /billing/subscription` —
 * exactly like the upgrade PATCH above, and unlike `extendTrial`, which applies
 * synchronously and is authoritative.
 *
 * Timing differs by action, and the UI copy depends on it:
 *   - **cancel** and **pause** take effect at `next_billing_period`. Paddle books
 *     a `scheduled_change` and does not emit its status webhook until the period
 *     ends, so the only observable change in the meantime is `scheduledChange`
 *     appearing on the two reads above.
 *   - **resume** is applied immediately (a status flip to ACTIVE).
 *   - **revoke-scheduled-change** is applied immediately too, but flips no
 *     status: it clears `scheduledChange` via a `subscription.updated` webhook
 *     that lands within seconds.
 *
 * Failure modes to branch on by `errorCode` (never message text), under
 * `ErrorCode.Billing.Lifecycle`: `041101 NoPaddleSubscription`,
 * `041102 NotActive` (cancel/pause), `041103 NotPaused` (resume),
 * `041105 ChangeAlreadyScheduled` (cancel/pause while one is already booked —
 * Paddle allows exactly one), `041106 NoScheduledChange` (revoke with nothing
 * pending), `041107 PauseAnchorUnknown` (a FIXED-LENGTH pause was asked for but
 * the subscription carries no period end to measure the months from) — all 422
 * preconditions — and `041104 RequestFailed` (502) when Paddle itself fails.
 *
 * One schema shared by all four: they are the same endpoint contract emitted by
 * one backend helper, not four that happen to look alike.
 */
export const lifecycleResponseSchema = z.object({
  status: z.string(),
});

export type LifecycleResponse = z.infer<typeof lifecycleResponseSchema>;

/**
 * The lifecycle actions — and, by construction, **the literal URL segment each
 * one POSTs to** (`/billing/subscription/${action}`).
 *
 * `revoke-scheduled-change` is not a bare verb like the other three, so it would
 * have been easy to add a `LifecycleAction → segment` lookup for the one odd
 * case. That is exactly what this union avoids: the member *is* the route, so a
 * mapping table can never drift out of sync with the backend path, and adding a
 * fifth action stays a one-line change. The cost is that the union member reads
 * as a path rather than a verb — deliberate, and the reason this comment exists.
 */
export type LifecycleAction = 'cancel' | 'pause' | 'resume' | 'revoke-scheduled-change';

/**
 * The fixed pause lengths `POST /billing/subscription/pause` accepts. The union
 * below is DERIVED from this array rather than hand-written beside it, so adding
 * or dropping a length is one edit and the picker's options (built from this
 * same list in `features/billing/lifecycle-helpers.ts`) cannot drift from what
 * the server will take. Anything outside it is rejected server-side — never
 * widen this to `number`. Must stay in lockstep with the api-side
 * `PAUSE_DURATION_MONTHS` in `billing-time.constants.ts`, which is the
 * authority — this copy exists only because the web app cannot import from
 * `apps/api`.
 *
 * Omitting the duration entirely means an INDEFINITE pause, which is a distinct
 * request rather than a member of this list: it has no length and, unlike a
 * fixed-length pause, needs no period end to anchor to (`041107`).
 */
export const PAUSE_DURATION_MONTHS = [1, 2, 3] as const;
export type PauseDurationMonths = (typeof PAUSE_DURATION_MONTHS)[number];

/**
 * `durationMonths` is meaningful for **pause** only, and only for a FIXED-length
 * one; omitted/`null` books the indefinite pause that was the sole behaviour
 * before the duration picker shipped.
 *
 * A body is sent only when there is something to put in it. An indefinite pause
 * — like cancel, resume and the revoke — stays the byte-identical body-less POST
 * all four have always been, because `apiFetch` attaches
 * `Content-Type: application/json` exactly when `init.body != null`, so omitting
 * the body omits the header with it. (The endpoint accepts a body-less POST, an
 * empty payload and `{}` alike — verified against a running `apps/api` — so this
 * is about keeping the request honest, not about working around a parser.)
 */
export async function runLifecycleAction(
  action: LifecycleAction,
  durationMonths?: PauseDurationMonths | null,
): Promise<LifecycleResponse> {
  const raw = await apiFetch<unknown>(`/billing/subscription/${action}`, {
    method: 'POST',
    ...(durationMonths != null ? { body: JSON.stringify({ durationMonths }) } : {}),
  });
  return lifecycleResponseSchema.parse(raw);
}

/**
 * Billing changelog — the OWNER-only history of what happened to this org's
 * subscription (`GET /billing/changelog`, keyset-paginated, newest first).
 *
 * Backed by the `billingChangelog` table. `title` and `description` are STORED
 * server-side at the moment each event happened, not derived at read time, so a
 * row keeps reading correctly even after a plan is renamed — the client renders
 * them verbatim and must not try to rebuild them.
 *
 * `type` is a plain `z.string()`, deliberately: new event kinds are added
 * regularly and an unknown one must degrade to a default icon rather than reject
 * the whole page at the schema boundary.
 *
 * `amount` is `null` for status-only events (cancel/pause/resume/expiry/payment
 * failure) and populated for the money-moving ones — that nullness is the signal
 * for whether to render a figure at all, so it must not be coerced to 0.
 */
export const changelogItemSchema = z.object({
  id: z.number(),
  type: z.string(),
  title: z.string(),
  description: z.string(),
  amount: moneySchema.nullable(),
  currency: z.string().nullable(),
  createdAt: z.string(),
});

export const changelogResponseSchema = z.object({
  items: z.array(changelogItemSchema),
  nextCursor: z.number().nullable(),
});

export type ChangelogItem = z.infer<typeof changelogItemSchema>;
export type ChangelogResponse = z.infer<typeof changelogResponseSchema>;

export async function getChangelog(params: {
  limit?: number;
  cursor?: number | null;
}): Promise<ChangelogResponse> {
  const query = new URLSearchParams();
  if (params.limit != null) query.set('limit', String(params.limit));
  if (params.cursor != null) query.set('cursor', String(params.cursor));
  const qs = query.toString();
  const raw = await apiFetch<unknown>(`/billing/changelog${qs ? `?${qs}` : ''}`);
  return changelogResponseSchema.parse(raw);
}

/**
 * Payment / invoice history — the OWNER-only billing-history read (P4).
 *
 * Backend source: `GET /billing/invoices` (`@RequiresOrgRole(OWNER)`), which
 * merges the org's local `payments` with Paddle transactions and returns them
 * sorted by `paidAt` descending. `amount` is in **major units** (`99` → $99.00),
 * NOT cents, so it reuses `moneySchema` (a `decimal` column may serialize as a
 * numeric string). `status` is a plain `z.string()` so a future status value
 * (beyond `completed | failed | refunded`) doesn't reject at the boundary.
 * `paidAt` and `invoiceUrl` are `.nullable()` — `invoiceUrl` is `null` for every
 * row today (fetching Paddle's per-transaction PDF is a deferred follow-up), so
 * the row renders a download affordance only when it is non-null.
 *
 * Validated at the boundary so a mis-shaped payload surfaces as a clean query
 * error instead of crashing the history table at render time.
 */
export const invoiceSchema = z.object({
  paddleTransactionId: z.string(),
  amount: moneySchema,
  currency: z.string(),
  status: z.string(),
  paidAt: z.string().nullable(),
  invoiceUrl: z.string().nullable(),
});

export type Invoice = z.infer<typeof invoiceSchema>;

export async function getInvoices(): Promise<Invoice[]> {
  // The API returns only the org's 5 most recent — the cut is server-side.
  const raw = await apiFetch<unknown>('/billing/invoices');
  return z.array(invoiceSchema).parse(raw);
}
